import React, { useEffect, useMemo, useState } from 'react'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../../common/SafeIcon'
import { createCourse, fetchCoursesForCurriculum } from '../../../config/courseService'
import {
  buildVideoEmbedUrl,
  createLessonRecord,
  createQuizForLesson,
  fetchLessonsForCourse
} from '../../../config/lessonService'
import { supabase, supabaseConfigError } from '../../../config/supabase'

const {
  FiUpload,
  FiDownload,
  FiInfo,
  FiFileText,
  FiCheckSquare,
  FiAlertTriangle,
  FiClipboard
} = FiIcons

const sanitizeFilename = (name = 'curriculum') => name.replace(/[^a-z0-9-]+/gi, '-').replace(/-+/g, '-').replace(/(^-|-$)/g, '')

const normalizeLessonPayload = (lesson, index, courseId, curriculumId, existingCount = 0) => ({
  title: lesson.title?.trim() || `Lesson ${index + 1}`,
  description: lesson.description?.trim() || '',
  subject: lesson.subject?.trim() || '',
  day_number: Number(lesson.day_number) || index + 1,
  order_index: typeof lesson.order_index === 'number' ? lesson.order_index : existingCount + index,
  video_url: buildVideoEmbedUrl(lesson.video_url || ''),
  content: lesson.content || '',
  attachments: lesson.attachments || lesson.attachments_metadata || [],
  course_id: courseId,
  curriculum_id: curriculumId
})

const parseAttachmentLine = (line) => {
  const cleaned = line.replace(/^-\s*/, '')
  if (!cleaned) return null
  const [name, url] = cleaned.split('|').map((part) => part.trim())
  if (!name && !url) return null
  return {
    name: name || 'Attachment',
    url: url || '',
    path: url || ''
  }
}

const parseQuizLine = (line) => {
  const cleaned = line.replace(/^-\s*/, '')
  const pieces = cleaned.split('|').map((piece) => piece.trim())
  const quiz = { type: 'multiple_choice', prompt: '', options: [], answer: undefined }

  pieces.forEach((piece) => {
    const [rawKey, ...rest] = piece.split(':')
    if (!rest.length) return
    const key = rawKey.trim().toLowerCase()
    const value = rest.join(':').trim()
    if (key === 'type') quiz.type = value
    if (key === 'prompt' || key === 'question') quiz.prompt = value
    if (key === 'answer') {
      if (value.toLowerCase() === 'true' || value.toLowerCase() === 'false') {
        quiz.answer = value.toLowerCase() === 'true'
      } else {
        quiz.answer = value
      }
    }
    if (key === 'options') {
      quiz.options = value.split(';').map((option) => {
        const trimmed = option.trim()
        const isCorrect = /\*$/.test(trimmed)
        return {
          text: trimmed.replace(/\*$/, '').trim(),
          isCorrect
        }
      })
    }
  })

  if (!quiz.prompt && !quiz.options.length && quiz.answer === undefined) return null
  if (quiz.type === 'multiple_choice' && !quiz.options.length) return null
  return quiz
}

const parseMarkdownImport = (input) => {
  const lines = input.split(/\r?\n/)
  let curriculumMeta = {}
  const courses = []
  let currentCourse = null
  let currentLesson = null
  let mode = null

  const pushLesson = () => {
    if (currentLesson && currentCourse) {
      currentLesson.order_index = currentCourse.lessons.length
      currentCourse.lessons.push(currentLesson)
    }
    currentLesson = null
    mode = null
  }

  const pushCourse = () => {
    pushLesson()
    if (currentCourse) {
      currentCourse.order_index = courses.length
      courses.push(currentCourse)
    }
    currentCourse = null
    mode = null
  }

  lines.forEach((rawLine) => {
    const line = rawLine.trim()
    if (!line) return

    if (line.startsWith('# ')) {
      pushCourse()
      curriculumMeta.name = line.replace(/^#\s*/, '').trim()
      mode = null
      return
    }

    if (line.startsWith('## ')) {
      pushCourse()
      currentCourse = {
        name: line.replace(/^##\s*/, '').replace(/^Course:\s*/i, '').trim(),
        description: '',
        estimated_duration: '',
        lessons: []
      }
      mode = null
      return
    }

    if (line.startsWith('### ')) {
      pushLesson()
      currentLesson = {
        title: line.replace(/^###\s*/, '').replace(/^Lesson:\s*/i, '').trim(),
        description: '',
        subject: '',
        day_number: currentCourse?.lessons.length + 1 || 1,
        content: '',
        attachments: [],
        quiz_schema: []
      }
      mode = null
      return
    }

    if (/^description:/i.test(line)) {
      const value = line.split(':').slice(1).join(':').trim()
      if (currentLesson) currentLesson.description = value
      else if (currentCourse) currentCourse.description = value
      else curriculumMeta.description = value
      mode = null
      return
    }

    if (/^subject:/i.test(line)) {
      const value = line.split(':').slice(1).join(':').trim()
      if (currentLesson) currentLesson.subject = value
      else if (currentCourse) currentCourse.subject = value
      else curriculumMeta.subject = value
      mode = null
      return
    }

    if (/^(grade|grade level):/i.test(line)) {
      curriculumMeta.grade_level = line.split(':').slice(1).join(':').trim()
      mode = null
      return
    }

    if (/^estimated duration:/i.test(line) && currentCourse) {
      currentCourse.estimated_duration = line.split(':').slice(1).join(':').trim()
      mode = null
      return
    }

    if (/^day\s*:/i.test(line) && currentLesson) {
      const day = Number(line.split(':').slice(1).join(':').trim())
      currentLesson.day_number = Number.isNaN(day) ? currentLesson.day_number : day
      mode = null
      return
    }

    if (/^video:/i.test(line) && currentLesson) {
      currentLesson.video_url = line.split(':').slice(1).join(':').trim()
      mode = null
      return
    }

    if (/^attachments?:/i.test(line) && currentLesson) {
      mode = 'attachments'
      return
    }

    if (/^quiz:/i.test(line) && currentLesson) {
      mode = 'quiz'
      return
    }

    if (/^content:/i.test(line) && currentLesson) {
      currentLesson.content = line.split(':').slice(1).join(':').trim()
      mode = 'content'
      return
    }

    if (mode === 'content' && currentLesson) {
      currentLesson.content = `${currentLesson.content ? `${currentLesson.content}\n` : ''}${line}`
      return
    }

    if (mode === 'attachments' && currentLesson && line.startsWith('-')) {
      const attachment = parseAttachmentLine(line)
      if (attachment) currentLesson.attachments.push(attachment)
      return
    }

    if (mode === 'quiz' && currentLesson && line.startsWith('-')) {
      const quiz = parseQuizLine(line)
      if (quiz) currentLesson.quiz_schema.push(quiz)
      return
    }
  })

  pushCourse()

  return {
    curriculum: curriculumMeta,
    courses
  }
}

const parseJsonImport = (input) => {
  const parsed = JSON.parse(input)
  const courses = (parsed.courses || []).map((course, idx) => ({
    ...course,
    order_index: course.order_index ?? idx,
    lessons: (course.lessons || []).map((lesson, lessonIdx) => ({
      ...lesson,
      order_index: lesson.order_index ?? lessonIdx,
      quiz_schema: lesson.quiz_schema || lesson.quiz || lesson.questions || []
    }))
  }))

  return {
    curriculum: parsed.curriculum || {},
    courses
  }
}

const parseImportPayload = (input) => {
  try {
    return parseJsonImport(input)
  } catch (jsonError) {
    try {
      return parseMarkdownImport(input)
    } catch (markdownError) {
      throw new Error('Unable to parse content as JSON or Markdown. Please review the schema and try again.')
    }
  }
}

const parseStructuredLessons = (input) => {
  const lines = input.split(/\r?\n/)
  const lessons = []
  let currentLesson = null

  const pushLesson = () => {
    if (!currentLesson) return
    const orderIndex = lessons.length
    lessons.push({
      ...currentLesson,
      order_index: orderIndex,
      day_number: currentLesson.day_number || orderIndex + 1,
      content: currentLesson.content?.trim() || ''
    })
  }

  lines.forEach((rawLine) => {
    const line = rawLine.trim()
    if (!line) {
      if (currentLesson?.content) currentLesson.content += '\n'
      return
    }

    const headingMatch = line.match(/^(#{1,6}\s+|lesson\s+\d+[:.-]\s+)(.+)/i)
    if (headingMatch) {
      pushLesson()
      currentLesson = {
        title: headingMatch[2].trim(),
        description: '',
        subject: '',
        content: ''
      }
      return
    }

    if (/^subject:/i.test(line)) {
      currentLesson = currentLesson || { title: 'Lesson', content: '' }
      currentLesson.subject = line.split(':').slice(1).join(':').trim()
      return
    }

    if (/^day:/i.test(line)) {
      currentLesson = currentLesson || { title: 'Lesson', content: '' }
      const dayValue = Number(line.split(':').slice(1).join(':').trim())
      currentLesson.day_number = Number.isNaN(dayValue) ? undefined : dayValue
      return
    }

    if (/^video:/i.test(line)) {
      currentLesson = currentLesson || { title: 'Lesson', content: '' }
      currentLesson.video_url = line.split(':').slice(1).join(':').trim()
      return
    }

    currentLesson = currentLesson || { title: 'Lesson', content: '' }
    currentLesson.content = `${currentLesson.content ? `${currentLesson.content}\n` : ''}${line}`
  })

  pushLesson()
  return lessons
}

const CurriculumImportExport = ({ curriculum, onImported }) => {
  const [importInput, setImportInput] = useState('')
  const [importPreview, setImportPreview] = useState(null)
  const [importError, setImportError] = useState(null)
  const [importing, setImporting] = useState(false)

  const [structuredInput, setStructuredInput] = useState('')
  const [structuredPreview, setStructuredPreview] = useState([])
  const [structuredCourseId, setStructuredCourseId] = useState('')
  const [structuredError, setStructuredError] = useState(null)
  const [structuredSaving, setStructuredSaving] = useState(false)

  const [courses, setCourses] = useState([])
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState(null)

  const curriculumId = curriculum?.id

  const schemaDescription = useMemo(() => ([
    'JSON schema: { curriculum: { name, description, grade_level, subject }, courses: [{ name, description, estimated_duration, lessons: [{ title, description, subject, day_number, content, video_url, attachments, quiz_schema }] }] }',
    'Markdown schema: use # Curriculum title, ## Course title, and ### Lesson title headings. Key-value lines like "Description:", "Estimated duration:", "Subject:", "Day:", "Video:" followed by optional "Attachments:" and "Quiz:" sections with bullet points.',
    'Quiz bullets accept pipes, e.g., "- type: multiple_choice | prompt: What is 2+2? | options: 4*;3;5". Use "answer:" for True/False or short answer questions.'
  ]), [])

  useEffect(() => {
    const loadCourses = async () => {
      if (!curriculumId || supabaseConfigError) {
        setCourses([])
        return
      }
      const { data } = await fetchCoursesForCurriculum(curriculumId)
      setCourses(data || [])
    }
    loadCourses()
  }, [curriculumId])

  const previewImport = () => {
    setImportError(null)
    try {
      const parsed = parseImportPayload(importInput)
      if (!parsed.courses?.length) {
        throw new Error('No courses were found in the provided content. Add at least one course heading.')
      }
      setImportPreview(parsed)
    } catch (error) {
      setImportError(error.message)
      setImportPreview(null)
    }
  }

  const handleImport = async () => {
    if (!curriculumId || !importPreview?.courses?.length) return
    if (supabaseConfigError) {
      setImportError(supabaseConfigError.message)
      return
    }

    try {
      setImporting(true)
      setImportError(null)

      const { data: existingCourses } = await fetchCoursesForCurriculum(curriculumId)
      const baseOrder = existingCourses?.length || 0

      for (const [courseIndex, course] of importPreview.courses.entries()) {
        const { data: createdCourse, error: courseError } = await createCourse(
          curriculumId,
          {
            name: course.name,
            description: course.description,
            estimated_duration: course.estimated_duration
          },
          baseOrder + courseIndex
        )
        if (courseError) throw courseError

        if (course.lessons?.length) {
          const { data: existingLessons } = await fetchLessonsForCourse(createdCourse.id)
          const lessonBase = existingLessons?.length || 0
          for (const [lessonIndex, lesson] of course.lessons.entries()) {
            const payload = normalizeLessonPayload(
              lesson,
              lessonIndex,
              createdCourse.id,
              curriculumId,
              lessonBase
            )
            const { data: createdLesson, error: lessonError } = await createLessonRecord(payload)
            if (lessonError) throw lessonError

            if (lesson.quiz_schema?.length) {
              const { error: quizError } = await createQuizForLesson(createdLesson.id, lesson.quiz_schema)
              if (quizError) throw quizError
            }
          }
        }
      }

      setImportPreview(null)
      setImportInput('')
      onImported?.()

      const { data: refreshedCourses } = await fetchCoursesForCurriculum(curriculumId)
      setCourses(refreshedCourses || [])
    } catch (error) {
      console.error('Import error', error)
      setImportError(error.message || 'Unable to import content right now.')
    } finally {
      setImporting(false)
    }
  }

  const handleStructuredPreview = () => {
    setStructuredError(null)
    try {
      const parsed = parseStructuredLessons(structuredInput)
      if (!parsed.length) throw new Error('No headings or lesson sections detected. Add headings like "Lesson 1: Fractions" or Markdown titles.')
      setStructuredPreview(parsed)
    } catch (error) {
      setStructuredPreview([])
      setStructuredError(error.message)
    }
  }

  const handleStructuredSave = async () => {
    if (!structuredCourseId) {
      setStructuredError('Select a course to attach these lessons.')
      return
    }
    if (!structuredPreview.length) {
      setStructuredError('Generate a preview before saving.')
      return
    }
    if (supabaseConfigError) {
      setStructuredError(supabaseConfigError.message)
      return
    }

    try {
      setStructuredSaving(true)
      setStructuredError(null)

      const { data: existingLessons, error: lessonFetchError } = await fetchLessonsForCourse(structuredCourseId)
      if (lessonFetchError) throw lessonFetchError
      const baseOrder = existingLessons?.length || 0

      for (const [index, lesson] of structuredPreview.entries()) {
        const payload = normalizeLessonPayload(
          { ...lesson, quiz_schema: [] },
          index,
          structuredCourseId,
          curriculumId,
          baseOrder
        )
        const { error: lessonError } = await createLessonRecord(payload)
        if (lessonError) throw lessonError
      }

      onImported?.()
      setStructuredInput('')
      setStructuredPreview([])
    } catch (error) {
      console.error('Structured import error', error)
      setStructuredError(error.message || 'Unable to save generated lessons.')
    } finally {
      setStructuredSaving(false)
    }
  }

  const buildCurriculumExport = async () => {
    if (!curriculumId) throw new Error('Select a curriculum to export.')
    const { data: curriculumRecord, error: curriculumError } = await supabase
      .from('curricula')
      .select('*')
      .eq('id', curriculumId)
      .single()
    if (curriculumError) throw curriculumError

    const { data: courseRecords, error: courseError } = await fetchCoursesForCurriculum(curriculumId)
    if (courseError) throw courseError
    const courseIds = courseRecords?.map((course) => course.id) || []

    const { data: lessonRecords, error: lessonError } = courseIds.length
      ? await supabase
          .from('lessons')
          .select('*')
          .in('course_id', courseIds)
          .order('order_index', { ascending: true })
      : { data: [], error: null }
    if (lessonError) throw lessonError

    const lessonIds = lessonRecords?.map((lesson) => lesson.id) || []
    const { data: quizRecords, error: quizError } = lessonIds.length
      ? await supabase.from('quizzes').select('*').in('lesson_id', lessonIds)
      : { data: [], error: null }
    if (quizError) throw quizError

    const quizMap = new Map()
    quizRecords?.forEach((quiz) => {
      quizMap.set(quiz.lesson_id, quiz.config || quiz.quiz_schema || [])
    })

    const coursesWithLessons = (courseRecords || []).map((course) => ({
      ...course,
      lessons: (lessonRecords || [])
        .filter((lesson) => lesson.course_id === course.id)
        .map((lesson) => ({
          ...lesson,
          quiz_schema: quizMap.get(lesson.id) || []
        }))
    }))

    return {
      curriculum: curriculumRecord,
      courses: coursesWithLessons
    }
  }

  const downloadJson = (data, filename) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleExport = async () => {
    if (supabaseConfigError) {
      setExportError(supabaseConfigError.message)
      return
    }
    try {
      setExporting(true)
      setExportError(null)
      const bundle = await buildCurriculumExport()
      downloadJson(bundle, `${sanitizeFilename(curriculum?.name || 'curriculum')}-export.json`)
    } catch (error) {
      console.error('Export error', error)
      setExportError(error.message || 'Unable to export this curriculum right now.')
    } finally {
      setExporting(false)
    }
  }

  const structuredCourseName = useMemo(
    () => courses.find((course) => course.id === structuredCourseId)?.name || 'selected course',
    [courses, structuredCourseId]
  )

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm">
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-2 text-gray-800 font-semibold">
          <SafeIcon icon={FiUpload} className="w-5 h-5 text-[#00AAFF]" />
          Curriculum import & export
        </div>
        {curriculumId && (
          <button
            onClick={handleExport}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
            disabled={exporting}
          >
            <SafeIcon icon={FiDownload} className={`w-4 h-4 ${exporting ? 'animate-spin' : ''}`} />
            Export JSON
          </button>
        )}
      </div>

      <div className="p-5 space-y-5">
        {exportError && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-lg text-red-700">
            <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 mt-0.5" />
            <div>
              <p className="font-semibold">Export issue</p>
              <p className="text-sm">{exportError}</p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
              <SafeIcon icon={FiFileText} className="w-4 h-4 text-[#00AAFF]" />
              Import JSON or Markdown
            </div>
            <textarea
              value={importInput}
              onChange={(e) => setImportInput(e.target.value)}
              rows={10}
              className="w-full border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00AAFF]"
              placeholder="Paste curriculum JSON or Markdown with #, ##, ### headings"
            />
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={previewImport}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
              >
                <SafeIcon icon={FiInfo} className="w-4 h-4" />
                Preview import
              </button>
              <button
                onClick={handleImport}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-[#00AAFF] text-white hover:bg-[#0099E5] disabled:opacity-50"
                disabled={!importPreview || importing}
              >
                <SafeIcon icon={FiUpload} className={`w-4 h-4 ${importing ? 'animate-spin' : ''}`} />
                Save to Supabase
              </button>
            </div>
            {importError && (
              <p className="text-sm text-red-600 flex items-center gap-2">
                <SafeIcon icon={FiAlertTriangle} className="w-4 h-4" />
                {importError}
              </p>
            )}
            {importPreview && (
              <div className="border border-gray-200 rounded-xl p-3 bg-gray-50 space-y-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
                  <SafeIcon icon={FiCheckSquare} className="w-4 h-4 text-green-600" />
                  Ready to import
                </div>
                <p className="text-sm text-gray-700">Courses detected: {importPreview.courses.length}</p>
                <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
                  {importPreview.courses.map((course) => (
                    <li key={course.name}>
                      <span className="font-semibold">{course.name || 'Untitled course'}</span> – {course.lessons?.length || 0} lesson(s)
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
              <SafeIcon icon={FiClipboard} className="w-4 h-4 text-[#00AAFF]" />
              Paste structured content
            </div>
            <p className="text-sm text-gray-600">Paste headings and sections (e.g., "Lesson 1:", Markdown ### titles, optional Subject/Day/Video lines). We&apos;ll detect lesson boundaries, show a summary, and save them into a selected course.</p>
            <div className="space-y-2">
              <select
                value={structuredCourseId}
                onChange={(e) => setStructuredCourseId(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00AAFF]"
              >
                <option value="">Choose a course</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>{course.name}</option>
                ))}
              </select>
              <textarea
                value={structuredInput}
                onChange={(e) => setStructuredInput(e.target.value)}
                rows={8}
                className="w-full border border-gray-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#00AAFF]"
                placeholder="### Lesson 1: Title\nSubject: Math\nContent lines..."
              />
              <div className="flex items-center justify-end gap-2">
                <button
                  onClick={handleStructuredPreview}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50"
                >
                  <SafeIcon icon={FiInfo} className="w-4 h-4" />
                  Generate preview
                </button>
                <button
                  onClick={handleStructuredSave}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg bg-[#00AAFF] text-white hover:bg-[#0099E5] disabled:opacity-50"
                  disabled={!structuredPreview.length || structuredSaving}
                >
                  <SafeIcon icon={FiUpload} className={`w-4 h-4 ${structuredSaving ? 'animate-spin' : ''}`} />
                  Save lessons
                </button>
              </div>
            </div>
            {structuredError && (
              <p className="text-sm text-red-600 flex items-center gap-2">
                <SafeIcon icon={FiAlertTriangle} className="w-4 h-4" />
                {structuredError}
              </p>
            )}
            {structuredPreview.length > 0 && (
              <div className="border border-gray-200 rounded-xl p-3 bg-gray-50 space-y-2">
                <p className="text-sm font-semibold text-gray-800">{structuredPreview.length} lesson(s) will be created in {structuredCourseName}.</p>
                <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
                  {structuredPreview.map((lesson, idx) => (
                    <li key={idx}>
                      <span className="font-semibold">{lesson.title || `Lesson ${idx + 1}`}</span>
                      {lesson.subject && ` · ${lesson.subject}`}
                      {lesson.day_number && ` · Day ${lesson.day_number}`}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 space-y-2">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
            <SafeIcon icon={FiInfo} className="w-4 h-4 text-[#00AAFF]" />
            Import schema tips
          </div>
          <ul className="text-sm text-gray-700 list-disc pl-5 space-y-1">
            {schemaDescription.map((item, idx) => (
              <li key={idx}>{item}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

export default CurriculumImportExport
